import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type {
  CSSProperties,
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from 'react';
import { statusTone } from '@ni/shared';
import { usePersonRecord } from './record';
import { useNetwork } from '../../api/hooks';
import { loadErrorLine } from '../../api/errors';
import { plural } from '../../lib/format';
import { Modal } from '../../components/ui/modal';
import {
  KIND_NOUN,
  NetworkDetail,
  nodeName,
  type NetworkSelection,
} from '../../components/network/detail-panel';
import { GraphMark } from '../../lib/graph-marks';
import { useMediaQuery } from '../../lib/use-media-query';
import {
  buildGraphModel,
  filterGraph,
  type NetworkFilter,
  clampZoom,
  createLayout,
  edgeTooltipText,
  faceScale,
  estimateText,
  incidentOf,
  layoutAspect,
  nodeStatusLabel,
  nodeTone,
  PHONE_GEOMETRY,
  scaleBox,
  statusesPresent,
  SUBJECT_RING_GAP,
  tagPosition,
  textSize,
  viewBoxFor,
  WIDE_GEOMETRY,
  ZOOM_FIT,
  ZOOM_LIMITS,
  zoomAbout,
  type Geometry,
  type GraphLink,
  type GraphModel,
  type GraphNode,
  type MeasureText,
  type NodeFace,
  type TextRole,
  type Zoom,
} from '../../lib/network-graph';
import { usePinch, wheelFactor, wheelIsForGraph } from '../../lib/graph-zoom';
import { useOrg } from '../../api/app-context';
import { subjectStatusLabel } from '../../lib/copy';
import { Loading } from '../../components/ui/loading';
import { EmptyLine } from '../../components/ui/empty-line';
import './network.css';

/**
 * The Network tab: who this record touches, drawn once.
 *
 * The read is one hop off the subject plus the closure among that hop, so the
 * picture has a fixed shape — the subject at the centre, everyone it names on a
 * ring, and the chords between ring nodes showing where the record already
 * knows two of them are connected. That shape is the whole reason this is a
 * drawing rather than another list: the chords are the thing a list cannot say.
 *
 * The layout is solved synchronously (network-graph.ts) with every name measured
 * at the size it is authored, and the view is fitted to the settled picture.
 * Nothing animates on load and the same record in the same panel draws the same
 * way on every visit; the only motion is a node being dragged.
 *
 * Scrolling over the graph, or pinching, zooms it about the pointer, with no
 * control on screen for it. Marks and names grow with the zoom up to reading
 * size and then hold while the picture keeps opening up (`faceScale`); zoomed
 * in, dragging the background pans.
 *
 * At rest the graph shows names and shapes only (blend spec, Network graph). A
 * line's words appear in a tooltip when the pointer is on it; what a line must
 * say unasked it says in its own stroke — dashed for sensitive, brick for
 * severe. Every name sits on a plate of the sheet, and the layout keeps lines
 * off other nodes' names, so no line or name collides.
 *
 * Pointing asks *who*; clicking asks *what and when*. A click on a node or a
 * line lights its ties and opens the detail panel (components/network/
 * detail-panel.tsx) beside the graph, and the graph stays live under it — no
 * scrim, nothing disabled — because reading a network means hopping from one
 * node to the next. Below the width where a second column fits, the same panel
 * comes up as a bottom sheet over the graph instead.
 */

/**
 * `useLayoutEffect` where there is a layout, and `useEffect` where there is not.
 * Both of this component's layout effects measure a box the browser has just
 * drawn, so on the client they must run before paint; on a server render there
 * is no box, and React warns once per render that a layout effect cannot be
 * encoded into static markup.
 */
const useMeasureEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/**
 * The tooltip's size before it has been measured — `max-width` and two lines of
 * its own type. Only ever a first guess: the real box is read back after mount.
 */
const TIP_ROOM = { w: 240, h: 56 };
/** How far the tooltip sits from the pointer. */
const TIP_OFFSET = 12;
/** A selected line's tag: its height, and the room either side of its words. */
const TAG_HEIGHT = 20;
const TAG_PADDING = 14;

/**
 * The drawing area assumed until the real one is measured. It only has to be
 * the right shape, and it is superseded before the first paint.
 */
const DEFAULT_SVG_SIZE = { w: 900, h: 620 };

/**
 * How far the pointer may travel between press and release and still count as a
 * click rather than a drag. Every node is draggable, so without this a reader
 * who nudges a node ends up with its panel open.
 */
const CLICK_SLOP = 4;

/** How long a resize must rest before the layout is solved again. */
const RESIZE_SETTLE_MS = 120;

/**
 * At or above this the detail panel sits beside the graph; below it, it comes up
 * as a bottom sheet over the graph (blend spec: the panel is a bottom sheet
 * under 1100px). The mockup's breakpoint, kept so the two read the same.
 */
const BESIDE_QUERY = '(min-width: 1100px)';

/** Phones set the graph a size down (`PHONE_GEOMETRY`). */
const PHONE_QUERY = '(max-width: 640px)';

/* ————————————————————————— measuring type ————————————————————————— */

/** The faces and weights each role is drawn in; network.css sets the same. */
const FONT: Record<TextRole, string> = {
  name: '450 {size}px Newsreader',
  subject: '500 {size}px Newsreader',
  status: '450 {size}px Inter',
  tag: '500 {size}px Inter',
};

let measuring: CanvasRenderingContext2D | null | undefined;

/**
 * Widths from a canvas, which shapes text exactly as the svg will. Where there
 * is no canvas (a server render, a test DOM) the layout's own estimate stands in.
 */
const measureText: MeasureText = (text, role, geometry) => {
  if (measuring === undefined) {
    measuring = null;
    if (typeof document !== 'undefined' && !/jsdom/i.test(navigator.userAgent)) {
      try {
        measuring = document.createElement('canvas').getContext('2d');
      } catch {
        measuring = null;
      }
    }
  }
  if (!measuring) return estimateText(text, role, geometry);
  measuring.font = FONT[role].replace('{size}', String(textSize(role, geometry)));
  return measuring.measureText(text).width;
};

const FONT_PROBES = ['450 13.5px Newsreader', '500 18px Newsreader', '450 11.5px Inter'];

/**
 * Whether the graph's faces have loaded, so names are measured in Newsreader
 * rather than in whatever stood in for it. False until they have; the layout is
 * solved again when this turns true.
 */
function useFontsReady(): boolean {
  const fonts = typeof document === 'undefined' ? undefined : document.fonts;
  const [ready, setReady] = useState(
    () => !fonts || FONT_PROBES.every((probe) => fonts.check(probe)),
  );
  useEffect(() => {
    if (ready || !fonts) return;
    let live = true;
    void Promise.all(FONT_PROBES.map((probe) => fonts.load(probe)))
      .catch(() => undefined)
      .then(() => {
        if (live) setReady(true);
      });
    return () => {
      live = false;
    };
  }, [ready, fonts]);
  return ready;
}

/* ————————————————————————— marks and names ————————————————————————— */

/**
 * The words under a mark, on their plate: the name, and — for a person the org
 * holds a subject record for — what that person is to it, a line under and a
 * size down.
 *
 * The plate is a rectangle of the sheet behind the text. Lines run under it, so
 * a line never cuts a name; and it stays opaque when the node is dimmed, so a
 * lit line passing behind a dimmed name does not show through it.
 *
 * Nothing is drawn for a status this build carries no word for: the mark keeps
 * its tone, and the caption is simply absent — `copy.ts`'s rule.
 */
export function NodeLabel({ node, face }: { node: GraphNode; face: NodeFace }) {
  return (
    <>
      <rect
        className="network-plate"
        x={face.plate.x}
        y={face.plate.y}
        width={face.plate.w}
        height={face.plate.h}
        rx={2}
      />
      <text
        className={node.name === null ? 'network-node-label deleted' : 'network-node-label'}
        y={face.baseline}
        textAnchor="middle"
      >
        {face.lines.map((line, index) => (
          <tspan key={index} x={0} dy={index === 0 ? 0 : face.lineStep}>
            {line}
          </tspan>
        ))}
        {face.status === null ? null : (
          <tspan className="network-node-status" x={0} dy={face.statusStep}>
            {face.status}
          </tspan>
        )}
      </text>
    </>
  );
}

const KIND_WORD: Record<GraphNode['kind'], string> = {
  person: 'Person',
  org: 'Organization',
  event: 'Event',
};

const KIND_PLURAL: Record<GraphNode['kind'], [string, string]> = {
  person: ['person', 'people'],
  org: ['organization', 'organizations'],
  event: ['event', 'events'],
};

/** A legend swatch: the graph's own mark, at the legend's fixed size. */
function Swatch({ kind, tone }: { kind: GraphNode['kind']; tone?: string }) {
  return (
    <svg
      className={`network-swatch ${kind}`}
      viewBox="-6 -6 12 12"
      aria-hidden="true"
      data-tone={tone}
    >
      <GraphMark kind={kind} r={kind === 'person' ? 4 : 3.8} />
    </svg>
  );
}

/**
 * The legend, which is also the filter: a press shows only that kind, or only
 * the people holding that status, and a second press shows everyone again.
 *
 * The status rows are the ones the *response* earned: a swatch per status
 * somebody on this canvas holds, in the vertical's own vocabulary order
 * (`statusesPresent`). A status this build has no word for takes no row.
 */
export function Legend({
  model,
  vertical,
  filter = null,
  onFilter,
}: {
  model: GraphModel;
  vertical: string;
  filter?: NetworkFilter | null;
  onFilter?: (filter: NetworkFilter) => void;
}) {
  const statuses = statusesPresent(model, vertical).flatMap((status) => {
    const label = subjectStatusLabel(vertical, status);
    return label === null ? [] : [{ status, label }];
  });
  return (
    <ul className="network-legend">
      {(['person', 'org', 'event'] as const).map((kind) => (
        <li key={kind} className={`network-legend-item ${kind}`}>
          <button
            type="button"
            aria-pressed={filter === `kind:${kind}`}
            onClick={() => onFilter?.(`kind:${kind}`)}
          >
            <Swatch kind={kind} />
            {KIND_WORD[kind]}
          </button>
        </li>
      ))}
      {statuses.length > 0 ? <li className="network-legend-gap" /> : null}
      {statuses.map(({ status, label }) => (
        <li key={status} className={`network-legend-item status-${status}`}>
          <button
            type="button"
            aria-pressed={filter === `status:${status}`}
            onClick={() => onFilter?.(`status:${status}`)}
          >
            <Swatch kind="person" tone={statusTone(vertical, status)} />
            {label}
          </button>
        </li>
      ))}
    </ul>
  );
}

/** "12 people", "6 organizations", "4 events": the subject not counted. */
function kindCounts(model: GraphModel): string[] {
  return (['person', 'org', 'event'] as const).map((kind) => {
    const words = KIND_PLURAL[kind];
    return plural(
      model.nodes.filter((node) => node.kind === kind && !node.isSubject).length,
      words[0],
      words[1],
    );
  });
}

/** What a screen reader gets instead of the drawing. */
function describe(model: GraphModel): string {
  const subject = model.nodes.find((node) => node.isSubject);
  const counts = kindCounts(model);
  const head = `Network of ${subject?.name ?? 'this record'}`;
  const parts = [...counts, plural(model.links.length, 'connection', 'connections')];
  // Commas, not the record's own ' · ' separator: this string is read aloud.
  return `${head}: ${parts.join(', ')}.`;
}

/* ————————————————————————— the drawing ————————————————————————— */

interface Tip {
  linkId: string;
  /** Pointer position in the panel's own padding box, in CSS pixels. */
  x: number;
  y: number;
  lines: string[];
}

/**
 * The drawing, and the detail panel it opens.
 *
 * Exported since the knowledge base, whose panel draws the same one hop around
 * whatever the reader selected (design note 2026-09-07 §1.4). It takes a model
 * and nothing else about where it is: `NetworkResponse` names its own centre, so
 * the same component draws a person's record and an organization's panel
 * without being told which it is looking at.
 */
export function Graph({
  model,
  alone,
  besideDetail,
}: {
  model: GraphModel;
  alone: boolean;
  /**
   * Where a selected node's detail goes, when the caller knows better than the
   * window does. The record's tab leaves this unset and asks `matchMedia`. The
   * knowledge base's panel passes `false` — it is *already* the narrow column
   * beside a list, so the detail comes up as a sheet however wide the window.
   */
  besideDetail?: boolean;
}) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const dragRef = useRef<string | null>(null);
  /* Where the pointer went down, and whether it has since travelled far enough
     that the gesture was a drag. A drag must not also select what it moved. */
  const pressRef = useRef<{ x: number; y: number } | null>(null);
  const movedRef = useRef(false);
  /* The mark or line the open panel was opened from, so closing can hand focus
     back to it rather than dropping it at the top of the document. */
  const openerRef = useRef<SVGGraphicsElement | null>(null);

  /* The org's vertical: what a person is to the org is said in its own words,
     and filled in its own tone. */
  const { vertical } = useOrg();
  const phone = useMediaQuery(PHONE_QUERY);
  const geometry: Geometry = phone ? PHONE_GEOMETRY : WIDE_GEOMETRY;
  const fontsReady = useFontsReady();
  const [svgSize, setSvgSize] = useState(DEFAULT_SVG_SIZE);
  /* The solve reads only the panel's shape, so a resize that keeps it does not
     solve the picture again. */
  const aspect = layoutAspect({ width: svgSize.w, height: svgSize.h });
  const layout = useMemo(() => {
    void fontsReady;
    return createLayout(
      model,
      { width: aspect, height: 1 },
      { vertical, geometry, measure: measureText },
    );
  }, [model, aspect, vertical, geometry, fontsReady]);
  const [dragging, setDragging] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [selected, setSelected] = useState<NetworkSelection | null>(null);
  const [tip, setTip] = useState<Tip | null>(null);
  const roomForBoth = useMediaQuery(BESIDE_QUERY);
  const beside = besideDetail ?? roomForBoth;
  const [filter, setFilter] = useState<NetworkFilter | null>(null);
  const visible = useMemo(() => filterGraph(model, filter), [model, filter]);
  const view = useMemo(
    () => viewBoxFor(layout.extent, { width: svgSize.w, height: svgSize.h }),
    [layout, svgSize],
  );
  const viewKey = `${layout.extent.x} ${layout.extent.y} ${view.w} ${view.h}`;
  /* The reader's zoom belongs to one fitted view: a new layout or a resized
     panel starts again from the fit. */
  const [zoomed, setZoomed] = useState<{ key: string; zoom: Zoom }>({ key: '', zoom: ZOOM_FIT });
  const zoom = zoomed.key === viewKey ? zoomed.zoom : ZOOM_FIT;
  const zoomGroupRef = useRef<SVGGElement | null>(null);
  /* Read by the wheel and pinch listeners, which are bound once. */
  const zoomState = useRef({ zoom, view, viewKey });
  zoomState.current = { zoom, view, viewKey };
  /* Where a pan of the zoomed picture began. */
  const panRef = useRef<{ x: number; y: number; zoom: Zoom; captured: boolean } | null>(null);
  /** How much a node's face is drawn at, in layout units, at this zoom. */
  const grow = faceScale(zoom.k, view.scale, geometry);

  function setZoom(next: Zoom) {
    /* Written through at once: several wheel or pinch events can land before
       the next render, and each must build on the one before it. */
    zoomState.current = { ...zoomState.current, zoom: next };
    setZoomed({ key: zoomState.current.viewKey, zoom: next });
  }

  /** Client pixels to viewBox units, outside the zoom. */
  function toViewPoint(clientX: number, clientY: number): { x: number; y: number } | null {
    const svg = svgRef.current;
    const ctm = svg?.getScreenCTM();
    if (!svg || !ctm) return null;
    const point = svg.createSVGPoint();
    point.x = clientX;
    point.y = clientY;
    const local = point.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  /* React binds wheel listeners passive, and a passive listener cannot keep the
     page still, so this one is bound by hand. A scroll the graph cannot use (it
     is at a limit that way) is left to the page. */
  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    const onWheel = (event: WheelEvent) => {
      const { zoom: current, view: box } = zoomState.current;
      const forGraph = wheelIsForGraph(
        event,
        current.k < ZOOM_LIMITS.max,
        current.k > ZOOM_LIMITS.min,
      );
      if (!forGraph) return;
      event.preventDefault();
      const at = toViewPoint(event.clientX, event.clientY);
      if (at) setZoom(zoomAbout(current, wheelFactor(event), at, box));
    };
    svg.addEventListener('wheel', onWheel, { passive: false });
    return () => svg.removeEventListener('wheel', onWheel);
    // Bound once; everything it reads is on `zoomState`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  usePinch(
    svgRef,
    (factor, clientX, clientY) => {
      const { zoom: current, view: box } = zoomState.current;
      const at = toViewPoint(clientX, clientY);
      if (at) setZoom(zoomAbout(current, factor, at, box));
    },
    () => {
      /* A second finger turns whatever the first began into a pinch: no drag,
         no pan, and no click on release. */
      movedRef.current = true;
      panRef.current = null;
      const id = dragRef.current;
      if (id) {
        dragRef.current = null;
        setDragging(null);
        layout.release(id);
        setMoves((n) => n + 1);
      }
    },
  );

  function toggleFilter(next: NetworkFilter) {
    setFilter((current) => (current === next ? null : next));
    setSelected(null);
    setHovered(null);
    cancelTipFrame();
    setTip(null);
  }
  // Bumped on every drag move; the positions live on the layout's own bodies,
  // so this is what tells React they have changed.
  const [moves, setMoves] = useState(0);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const [tipSize, setTipSize] = useState(TIP_ROOM);
  const frameRef = useRef<number | null>(null);
  const pendingRef = useRef<{ link: GraphLink; x: number; y: number } | null>(null);

  /* The drawing area's real size, which is the size the layout is solved at.
     Measured synchronously first so the first paint is at the right size; the
     observer handles later resizes. */
  useMeasureEffect(() => {
    const element = svgRef.current;
    if (!element) return;

    const measure = (w: number, h: number) => {
      if (w <= 0 || h <= 0) return;
      const size = { w: Math.round(w), h: Math.round(h) };
      setSvgSize((current) => (current.w === size.w && current.h === size.h ? current : size));
    };

    const rect = element.getBoundingClientRect();
    measure(rect.width, rect.height);

    /* Later sizes are settled before they are solved for: a window being
       dragged reports a size every frame, and a crowded event's solve is a
       few hundred milliseconds of main thread each time. */
    if (typeof ResizeObserver === 'undefined') return;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (!box) return;
      clearTimeout(timer);
      timer = setTimeout(() => measure(box.width, box.height), RESIZE_SETTLE_MS);
    });
    observer.observe(element);
    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, []);

  /* A refetch can land while the pointer is over a line that the new response
     no longer has. The highlight and the tooltip go with the old picture. */
  useEffect(() => {
    cancelTipFrame();
    setTip(null);
    setHovered(null);
    setDragging(null);
  }, [model]);

  /* The selection is *not* cleared with them. The read polls, so a response
     that came back identical must not close a panel the reader is in the
     middle of; only a selection the new picture no longer contains goes — and
     through the same door a reader's own close does, so focus is put back. */
  useEffect(() => {
    if (!selected) return;
    const alive =
      selected.kind === 'node'
        ? model.nodes.some((node) => node.id === selected.id)
        : model.links.some((link) => link.id === selected.id);
    if (!alive) closeDetail();
  }, [model, selected]);

  /* The tooltip's own box, read back once it is on screen. */
  useMeasureEffect(() => {
    const element = tipRef.current;
    if (!element) return;
    const rect = element.getBoundingClientRect();
    setTipSize((current) =>
      Math.abs(current.w - rect.width) < 0.5 && Math.abs(current.h - rect.height) < 0.5
        ? current
        : { w: rect.width, h: rect.height },
    );
  }, [tip?.linkId]);

  useEffect(() => cancelTipFrame, []);

  const positions = useMemo(() => {
    void moves;
    return new Map(layout.points().map((point) => [point.id, point]));
  }, [layout, moves]);

  /* What is lit, in order of how specific the gesture was: a pointer literally
     on a line, then a pointer on (or dragging) a mark, then — with the pointer
     off the picture entirely — whatever is selected. */
  const target = tip
    ? { activeNodeId: null, activeLinkId: tip.linkId }
    : (dragging ?? hovered)
      ? { activeNodeId: dragging ?? hovered, activeLinkId: null }
      : selected?.kind === 'link'
        ? { activeNodeId: null, activeLinkId: selected.id }
        : { activeNodeId: selected?.id ?? null, activeLinkId: null };
  const { activeNodeId, activeLinkId } = target;

  /* Which id is lit, not which tooltip is open: the tooltip's coordinates change
     on every pointer move, and depending on the whole object here would
     re-render every node and line sixty times a second. */
  const incident = useMemo(
    () => incidentOf(model, { activeNodeId, activeLinkId }),
    [activeNodeId, activeLinkId, model],
  );
  const lit = incident.nodes.size > 0;

  /** Client pixels to the coordinate system the layout solved in, through the zoom. */
  function toLayoutPoint(event: ReactPointerEvent): { x: number; y: number } | null {
    const svg = svgRef.current;
    const ctm = zoomGroupRef.current?.getScreenCTM();
    if (!svg || !ctm) return null;
    const point = svg.createSVGPoint();
    point.x = event.clientX;
    point.y = event.clientY;
    const local = point.matrixTransform(ctm.inverse());
    return { x: local.x, y: local.y };
  }

  /**
   * Every press on the picture, wherever it lands, recorded in the capture
   * phase so it runs before the node's own handler and also covers the marks
   * that do not drag (the subject) and the lines, which never do.
   */
  function notePress(event: ReactPointerEvent) {
    pressRef.current = { x: event.clientX, y: event.clientY };
    movedRef.current = false;
  }

  function startDrag(event: ReactPointerEvent<SVGGElement>, id: string) {
    // The subject is the page: it stays where the reader last saw it.
    if (id === model.subjectId) return;
    /* At the fit one finger scrolls the page, wherever it lands: a touch drags
       a node only once the picture is zoomed (and the svg claims the finger). */
    if (event.pointerType === 'touch' && zoom.k <= ZOOM_LIMITS.min) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = id;
    setDragging(id);
    setTip(null);
  }

  /**
   * A press on the zoomed picture that is not on a node is the start of a pan.
   * Runs after the node's own handler, which has claimed the press if it drags.
   */
  function startPan(event: ReactPointerEvent) {
    if (dragRef.current || zoom.k <= ZOOM_LIMITS.min || event.button !== 0) return;
    panRef.current = { x: event.clientX, y: event.clientY, zoom, captured: false };
  }

  function moveDrag(event: ReactPointerEvent<SVGSVGElement>) {
    /* Before the drag check: the subject does not drag and a line never did, so
       a press that travels on either would otherwise open a panel on release. */
    const press = pressRef.current;
    if (press && Math.hypot(event.clientX - press.x, event.clientY - press.y) > CLICK_SLOP) {
      movedRef.current = true;
    }

    const pan = panRef.current;
    if (pan && movedRef.current) {
      /* Captured only once it is a pan, so a press that stays put is still an
         ordinary click on whatever it landed on. */
      if (!pan.captured) {
        event.currentTarget.setPointerCapture(event.pointerId);
        pan.captured = true;
      }
      setTip(null);
      setZoom(
        clampZoom(
          {
            k: pan.zoom.k,
            x: pan.zoom.x + (event.clientX - pan.x) / view.scale,
            y: pan.zoom.y + (event.clientY - pan.y) / view.scale,
          },
          view,
        ),
      );
      return;
    }

    const id = dragRef.current;
    if (!id || !movedRef.current) return;
    const point = toLayoutPoint(event);
    if (!point) return;
    layout.move(id, point.x, point.y);
    setMoves((n) => n + 1);
  }

  /**
   * The release, for every press. A node that was moved rejoins the forces
   * rather than staying pinned where it was dropped, so the picture settles
   * around it once more.
   */
  function endPress() {
    pressRef.current = null;
    panRef.current = null;
    const id = dragRef.current;
    if (!id) return;
    dragRef.current = null;
    setDragging(null);
    if (movedRef.current) {
      layout.release(id);
      setMoves((n) => n + 1);
    }
  }

  /** Open the panel on what was pressed — unless the press turned into a drag. */
  function select(next: NetworkSelection, from: SVGGraphicsElement | null) {
    if (movedRef.current) return;
    openerRef.current = from;
    setSelected(next);
  }

  function closeDetail() {
    setSelected(null);
    // Back where it came from; the drawing itself when the opener is gone.
    const opener = openerRef.current;
    openerRef.current = null;
    if (opener && opener.isConnected) opener.focus?.();
    else svgRef.current?.focus?.();
  }

  /** Enter and Space press a thing that is not a button but behaves as one. */
  function onActivate(event: ReactKeyboardEvent<SVGGraphicsElement>, next: NetworkSelection) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    openerRef.current = event.currentTarget;
    setSelected(next);
  }

  /**
   * Follow the pointer along a line, at most once a frame: `pointermove` fires
   * far faster than the screen repaints.
   */
  function showTip(event: ReactPointerEvent, link: GraphLink) {
    const panel = panelRef.current;
    if (!panel) return;

    const rect = panel.getBoundingClientRect();
    pendingRef.current = {
      link,
      // The border box, less the border: the tooltip is placed in the padding box.
      x: event.clientX - rect.left - panel.clientLeft + panel.scrollLeft,
      y: event.clientY - rect.top - panel.clientTop + panel.scrollTop,
    };

    if (frameRef.current !== null) return;
    frameRef.current = requestAnimationFrame(() => {
      frameRef.current = null;
      const next = pendingRef.current;
      if (!next) return;
      setTip({ linkId: next.link.id, x: next.x, y: next.y, lines: edgeTooltipText(next.link) });
    });
  }

  function cancelTipFrame() {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
    pendingRef.current = null;
  }

  function hideTip(linkId: string) {
    cancelTipFrame();
    setTip((current) => (current && current.linkId === linkId ? null : current));
  }

  const at = (id: string) => positions.get(id) ?? { x: 0, y: 0 };
  /* The layout covers every node of the model, and the drawing only ever a
     filtered subset of them. */
  const faceOf = (node: GraphNode): NodeFace => layout.faces.get(node.id)!;

  function edgeClass(link: GraphLink): string {
    const classes = ['network-edge'];
    classes.push(
      link.source === model.subjectId || link.target === model.subjectId ? 'spoke' : 'chord',
    );
    if (link.isSensitive) classes.push('sensitive');
    if (link.isSevere) classes.push('severe');
    if (lit) classes.push(incident.links.has(link.id) ? 'hot' : 'dim');
    if (selected?.kind === 'link' && selected.id === link.id) classes.push('is-selected');
    return classes.join(' ');
  }

  /* A selected line names itself once, on the line, clear of every name —
     unless the pointer is on it, when the tooltip is already saying so. */
  const selectedLink =
    selected?.kind === 'link' && tip?.linkId !== selected.id
      ? visible.links.find((link) => link.id === selected.id)
      : undefined;
  const tag = selectedLink
    ? (() => {
        const words = edgeTooltipText(selectedLink)[0]!;
        const text = words.charAt(0).toUpperCase() + words.slice(1);
        const w = measureText(text, 'tag', geometry) + TAG_PADDING;
        const where = tagPosition(
          at(selectedLink.source),
          at(selectedLink.target),
          { w: w * grow, h: TAG_HEIGHT * grow },
          visible.nodes.map((node) => ({ at: at(node.id), box: scaleBox(faceOf(node).box, grow) })),
        );
        return { text, w, ...where };
      })()
    : null;

  /* Placed beside the pointer, then held inside the panel: clamping rather than
     flipping keeps the tooltip on the same side of the cursor wherever it is. */
  const visibleLeft = panelRef.current?.scrollLeft ?? 0;
  const visibleTop = panelRef.current?.scrollTop ?? 0;
  const visibleWidth = panelRef.current?.clientWidth ?? svgSize.w;
  const visibleHeight = panelRef.current?.clientHeight ?? svgSize.h;
  const tipAt = tip
    ? {
        left: clamp(
          tip.x + TIP_OFFSET,
          visibleLeft,
          Math.max(visibleLeft, visibleLeft + visibleWidth - tipSize.w),
        ),
        top: clamp(
          tip.y + TIP_OFFSET,
          visibleTop,
          Math.max(visibleTop, visibleTop + visibleHeight - tipSize.h),
        ),
      }
    : null;

  /* `handlesEscape` is the panel's own window listener. Inside `Modal` it is
     off, because the modal binds the same key on the same window. */
  const detail = (handlesEscape: boolean) =>
    selected === null ? null : (
      <NetworkDetail
        model={model}
        selection={selected}
        subjectId={model.subjectId}
        vertical={vertical}
        onClose={closeDetail}
        onSelectNode={(id) => setSelected({ kind: 'node', id })}
        handlesEscape={handlesEscape}
      />
    );

  /* The type sizes the layout measured at, handed to the stylesheet, so a name
     is drawn at exactly the size its footprint was measured for. */
  const typeSizes = {
    '--network-label': `${geometry.label}px`,
    '--network-subject': `${geometry.subject}px`,
    '--network-status': `${geometry.statusSize}px`,
  } as CSSProperties;

  return (
    <div className={beside ? 'network-layout beside' : 'network-layout'}>
      <div className="network-main">
        {alone ? null : (
          <div className="network-tools">
            <Legend model={model} vertical={vertical} filter={filter} onFilter={toggleFilter} />
            <p className="network-count">{kindCounts(model).join(' · ')}</p>
          </div>
        )}

        <div className={alone ? 'network-panel alone' : 'network-panel'} ref={panelRef}>
          <svg
            ref={svgRef}
            className={['network-svg', lit ? 'lit' : '', zoom.k > ZOOM_LIMITS.min ? 'zoomed' : '']
              .filter(Boolean)
              .join(' ')}
            style={typeSizes}
            viewBox={`${round(view.x)} ${round(view.y)} ${round(view.w)} ${round(view.h)}`}
            preserveAspectRatio="xMidYMid meet"
            /* A `group`, not an `img`: every node and line inside is a focusable
               control with its own label, and `img` prunes its children out of
               the accessibility tree. `tabIndex` -1 gives a panel closed after
               its opener has gone somewhere to put focus. */
            role="group"
            tabIndex={-1}
            aria-label={describe(visible)}
            onPointerDownCapture={notePress}
            onPointerDown={startPan}
            onPointerMove={moveDrag}
            onPointerUp={endPress}
            onPointerCancel={endPress}
          >
            <g ref={zoomGroupRef} transform={`translate(${zoom.x} ${zoom.y}) scale(${zoom.k})`}>
              <g className="network-edges">
                {visible.links.map((link) => {
                  const a = at(link.source);
                  const b = at(link.target);
                  return (
                    <line
                      key={link.id}
                      className={edgeClass(link)}
                      x1={a.x}
                      y1={a.y}
                      x2={b.x}
                      y2={b.y}
                    />
                  );
                })}
              </g>

              {/* The hit targets: a fat transparent stroke over each line. Above the
              drawn edges so it wins over them, below the nodes so a mark under
              the pointer still belongs to its node. */}
              <g className="network-edge-hits">
                {visible.links.map((link) => {
                  const a = at(link.source);
                  const b = at(link.target);
                  return (
                    <line
                      key={link.id}
                      className="network-edge-hit"
                      x1={a.x}
                      y1={a.y}
                      x2={b.x}
                      y2={b.y}
                      tabIndex={0}
                      role="button"
                      aria-label={`Connection between ${nodeName(model.nodes.find((node) => node.id === link.source)!)} and ${nodeName(model.nodes.find((node) => node.id === link.target)!)}: ${link.label}`}
                      onPointerEnter={(event) => showTip(event, link)}
                      onPointerMove={(event) => showTip(event, link)}
                      onPointerLeave={() => hideTip(link.id)}
                      onClick={(event) =>
                        select({ kind: 'link', id: link.id }, event.currentTarget)
                      }
                      onKeyDown={(event) => onActivate(event, { kind: 'link', id: link.id })}
                    />
                  );
                })}
              </g>

              <g className="network-nodes">
                {visible.nodes.map((node) => {
                  const point = at(node.id);
                  const face = faceOf(node);
                  const tone = nodeTone(node, vertical);
                  const classes = ['network-node', node.kind, tone === null ? 'known' : 'tracked'];
                  if (node.isSubject) classes.push('subject');
                  if (node.id === dragging) classes.push('dragging');
                  if (lit) classes.push(incident.nodes.has(node.id) ? 'hot' : 'dim');
                  if (selected?.kind === 'node' && selected.id === node.id)
                    classes.push('is-selected');

                  return (
                    <g
                      key={node.id}
                      className={classes.join(' ')}
                      data-tone={tone ?? undefined}
                      transform={`translate(${point.x} ${point.y})${grow === 1 ? '' : ` scale(${grow})`}`}
                      tabIndex={0}
                      role="button"
                      /* Read aloud as a name, a kind and a status: what the shape,
                       the fill and the caption say to everyone else. */
                      aria-label={[
                        nodeName(node),
                        KIND_NOUN[node.kind],
                        nodeStatusLabel(node, vertical),
                      ]
                        .filter(Boolean)
                        .join(', ')}
                      onPointerDown={(event) => startDrag(event, node.id)}
                      onPointerEnter={() => setHovered(node.id)}
                      onPointerLeave={() =>
                        setHovered((current) => (current === node.id ? null : current))
                      }
                      onClick={(event) =>
                        select({ kind: 'node', id: node.id }, event.currentTarget)
                      }
                      onKeyDown={(event) => onActivate(event, { kind: 'node', id: node.id })}
                    >
                      {node.isSubject ? (
                        <GraphMark
                          kind={node.kind}
                          r={face.r + SUBJECT_RING_GAP}
                          className="network-ring"
                        />
                      ) : null}
                      <GraphMark kind={node.kind} r={face.r} className="network-mark" />
                      <NodeLabel node={node} face={face} />
                    </g>
                  );
                })}
              </g>

              {tag ? (
                <g
                  className="network-edge-tag"
                  transform={`translate(${tag.x} ${tag.y})${grow === 1 ? '' : ` scale(${grow})`}`}
                >
                  <rect
                    x={-tag.w / 2}
                    y={-TAG_HEIGHT / 2}
                    width={tag.w}
                    height={TAG_HEIGHT}
                    rx={2}
                  />
                  <text y={4} textAnchor="middle">
                    {tag.text}
                  </text>
                </g>
              ) : null}
            </g>
          </svg>

          {tip && tipAt ? (
            /* Hidden from assistive technology because it is a preview of a
               surface reachable without it: every line is a focusable control
               with the same words in its `aria-label`, and Enter on one opens
               the panel. */
            <div ref={tipRef} className="network-tip" aria-hidden="true" style={tipAt}>
              {tip.lines.map((line, index) => (
                <div key={index} className={index === 0 ? undefined : 'network-tip-note'}>
                  {line}
                </div>
              ))}
            </div>
          ) : null}

          {alone ? <div className="network-caption">No connections recorded yet.</div> : null}
        </div>
      </div>

      {/* Beside the graph where there is room for a column, and a bottom sheet
          over it where there is not. Never both: the panel manages focus and its
          open facts each fetch their sources, so a second mounted copy would do
          all of it twice. */}
      {beside ? (
        <aside className="network-aside" aria-label="Connection detail">
          {selected ? (
            detail(true)
          ) : (
            <p className="network-detail-empty">Select a person, organization, or connection.</p>
          )}
        </aside>
      ) : selected === null ? null : (
        <Modal label="Connection detail" className="network-sheet" onClose={closeDetail}>
          {detail(false)}
        </Modal>
      )}
    </div>
  );
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(Math.max(value, low), high);

const round = (value: number): number => Math.round(value * 100) / 100;

/* ————————————————————————— the tab ————————————————————————— */

/**
 * **The one-hop reading of a record**, given the record rather than reading it
 * off a person's outlet context (organisations-as-subjects note §2 chunk 2b).
 *
 * `GET /subjects/:id/network` has taken an organisation since chunk 2a — the
 * projection below the person gate was already kind-blind — so what stood
 * between an organisation and this drawing was one line reading
 * `usePersonRecord()`. Lifting the id into a prop is the whole of the change:
 * the tab below passes the person's, the organisation's record page passes its
 * own, and there is one graph rather than two that will drift.
 */
export function NetworkView({ subjectId }: { subjectId: string }) {
  const network = useNetwork(subjectId);

  const model = useMemo(
    () => (network.data ? buildGraphModel(network.data) : null),
    [network.data],
  );

  if (network.isPending)
    return (
      <EmptyLine>
        <Loading />
      </EmptyLine>
    );
  if (network.isError) return <EmptyLine>{loadErrorLine(network.error)}</EmptyLine>;
  if (!model || model.nodes.length === 0) {
    return <EmptyLine>No connections recorded yet.</EmptyLine>;
  }

  /* A record with nothing on it still gets its picture — the subject, alone,
     said once and quietly under it. A sentence over an empty 480px box would
     read as something that failed to load. */
  const alone = model.links.length === 0;

  return (
    <div className="section">
      {/* The panel is the graph's, not the tab's: the tooltip is positioned
          against it, so the two have to be in one component. */}
      <Graph model={model} alone={alone} />
    </div>
  );
}

/** The person record's Network tab: the same drawing, over the person's id. */
export function NetworkTab() {
  const { subjectId } = usePersonRecord();
  return <NetworkView subjectId={subjectId} />;
}
